import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Building2, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export function SettingsCostCentersSection({ companyId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", kind: "center", active: true });

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await apiClient.get(`/companies/${companyId}/cost-centers`);
      setItems(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [companyId]); // eslint-disable-line

  const create = async (e) => {
    e.preventDefault();
    if (!form.code || !form.name) { toast.error("Code + Name pflicht."); return; }
    try {
      await apiClient.post(`/companies/${companyId}/cost-centers`, form);
      setForm({ code: "", name: "", kind: "center", active: true });
      toast.success("Angelegt.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Anlegen fehlgeschlagen.");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Wirklich löschen?")) return;
    try {
      await apiClient.delete(`/companies/${companyId}/cost-centers/${id}`);
      toast.success("Gelöscht.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen nicht möglich.");
    }
  };

  return (
    <Card className="p-5 border border-slate-200 bg-white" id="section-cost-centers" data-testid="settings-cost-centers-section">
      <div className="flex items-start gap-3 mb-3">
        <div className="h-9 w-9 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center flex-shrink-0">
          <Building2 className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-sm font-medium text-slate-900">Kostenstellen &amp; Kostenträger</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Nutze Kostenstellen (Abteilungen, Standorte) oder Kostenträger (Projekte, Produkte) für interne Auswertungen.
            Buchungen können diesen Dimensionen zugeordnet werden. Bexio hat Kostenstellen 2026 entfernt — du hast sie! 🎯
          </p>
        </div>
      </div>

      <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-3 items-end" data-testid="cost-center-form">
        <div className="space-y-1">
          <Label className="text-[11px]">Code</Label>
          <Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} placeholder="z.B. KST1" data-testid="cc-code-input" />
        </div>
        <div className="space-y-1 md:col-span-2">
          <Label className="text-[11px]">Name</Label>
          <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="z.B. Vertrieb / Projekt-Alpha" data-testid="cc-name-input" />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">Typ</Label>
          <select value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))} className="w-full text-sm rounded-md border border-slate-200 px-2 py-1.5" data-testid="cc-kind-select">
            <option value="center">Kostenstelle</option>
            <option value="object">Kostenträger</option>
          </select>
        </div>
        <Button type="submit" size="sm" className="rounded-full bg-slate-900 text-slate-50" data-testid="cc-create-button">
          <Plus className="h-3 w-3 mr-1" /> Anlegen
        </Button>
      </form>

      {loading && <div className="text-xs text-slate-500 italic">Lade …</div>}
      {items.length === 0 && !loading && (
        <div className="text-[11px] text-slate-500 italic" data-testid="cc-empty">Noch keine Kostenstellen/Kostenträger.</div>
      )}
      {items.length > 0 && (
        <div className="space-y-1">
          {items.map((cc) => (
            <div key={cc._id} className="flex items-center justify-between gap-2 p-2 rounded bg-slate-50" data-testid={`cc-row-${cc._id}`}>
              <div className="flex items-center gap-2">
                <Badge className={cc.kind === "center" ? "bg-teal-100 text-teal-800" : "bg-indigo-100 text-indigo-800"}>
                  {cc.kind === "center" ? "Stelle" : "Träger"}
                </Badge>
                <code className="font-mono text-xs">{cc.code}</code>
                <span className="text-sm">{cc.name}</span>
                {!cc.active && <span className="text-[10px] text-slate-500">(inaktiv)</span>}
              </div>
              <Button type="button" size="sm" variant="outline" className="rounded-full text-rose-700 hover:bg-rose-50"
                onClick={() => del(cc._id)} data-testid={`cc-delete-${cc._id}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
